import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, isBackendConfigured } from '../utils/backendApi';
import { StatusBadge, APP_TYPES } from './org/AppsModule';
import KitIpDiagram from '../components/KitIpDiagram';
import AdminReleases from './AdminReleases';
import AdminPlatformHealth from './AdminPlatformHealth';
import AdminFleetByOrg from './AdminFleetByOrg';
import AdminFleetCommand from './AdminFleetCommand';
import AdminMailPlanes from './AdminMailPlanes';
import useCommandToggle, { CommandToggle } from '../components/command/useCommandToggle';
import { Spark, useVitals, hostMemory } from '../components/VitalsSpark';
import './AdminApps.css';

// Operator fleet admin (/admin/apps) — instances, fleet health board,
// security scans, instance hosts, and the live price book. Every call here
// hits the /apps/admin/* endpoints, which 403 non-operators (founder
// allowlist + apps.operators in live config) — the UI renders that honestly.

const ACTIONS = ['suspend', 'resume', 'terminate', 'reprovision'];
const TABS = ['launch', 'releases', 'platform', 'instances', 'health', 'costs', 'scans', 'hosts', 'mail', 'pricebook', 'ipmap'];
const TAB_LABELS = {
  launch: 'Launch',
  releases: 'Releases',
  platform: 'Platform',
  instances: 'Instances',
  health: 'Health',
  costs: 'Costs',
  scans: 'Scans',
  hosts: 'Hosts',
  mail: 'Mail planes',
  pricebook: 'Price book',
  ipmap: 'IP map',
};
const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
}

function fmtAgo(ts) {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function StageList({ job }) {
  if (!job) return <p className="cs-help">No provision job recorded.</p>;
  return (
    <div className="aa-job">
      <div className="aa-job-head">
        {job.kind} — {job.status}
      </div>
      <ol className="aa-stages">
        {(job.stages || []).map((s) => (
          <li key={s.name} className={`aa-stage is-${s.status}`}>
            <span className="aa-stage-name">{s.name}</span>
            <span className="aa-stage-status">{s.status}</span>
            {s.detail ? <span className="aa-stage-detail">{s.detail}</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

// License phone-home chip: status + freshness (a live instance validates every
// 12h — silent past 24h is worth a look). '—' = no license issued yet.
function LicenseChip({ license }) {
  if (!license) return <span className="cs-help" style={{ margin: 0 }}>—</span>;
  const ago = license.lastSeenAt ? Date.now() - license.lastSeenAt : null;
  const fresh = ago != null && ago < 24 * 60 * 60 * 1000;
  const agoTxt = ago == null ? 'never seen' : ago < 60 * 60 * 1000
    ? `${Math.max(1, Math.round(ago / 60000))}m ago`
    : `${Math.round(ago / 3600000)}h ago`;
  const cls = license.status !== 'active' ? 'is-err' : fresh ? 'is-ok' : 'is-warn';
  return (
    <span className={`ap-badge ${cls}`} title={`term ends ${license.termEnd ? new Date(license.termEnd).toLocaleDateString() : '—'}`}>
      {license.status} · {agoTxt}
    </span>
  );
}

function InstanceRow({ inst, onAction, onRevokeLicense, busy }) {
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [appType, setAppType] = useState(inst.template?.appType || 'blank');
  const [appTypeMsg, setAppTypeMsg] = useState('');

  const saveAppType = async () => {
    setAppTypeMsg('');
    try {
      await apiPost(`/apps/admin/instances/${inst.orgId}/${inst.appId}/app-type`, { appType });
      setAppTypeMsg('saved — reprovision to apply the template on the instance');
    } catch (err) {
      setAppTypeMsg(`failed: ${err.message}`);
    }
  };

  const loadJob = useCallback(async () => {
    try {
      const data = await apiGet(`/apps/${inst.orgId}/${inst.appId}/status`);
      setJob(data.job);
    } catch (err) {
      setJob(null);
    }
  }, [inst.orgId, inst.appId]);

  useEffect(() => {
    if (open) loadJob();
  }, [open, loadJob]);

  const act = (action) => {
    if (action === 'terminate') {
      setConfirming(true);
      return;
    }
    if (action === 'site-mode') {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Put ${inst.slug} in site mode?\n\nThe public site is snapshotted and served statically from the host edge; the whole stack stops (data stays on disk, a backup is taken first). Sign-in, the app, Dev Console, pushes and the assistant are off until you switch back. The UAT twin is suspended.`)) return;
      onAction(inst, 'site-mode');
      return;
    }
    if (action === 'brain-pull') {
      onAction(inst, 'brain-pull');
      return;
    }
    if (action === 'domain') {
      // eslint-disable-next-line no-alert
      const domain = window.prompt('Custom apex for this instance (bare host, no www). Empty reverts to the managed wildcard.\nDNS first: A @ → host IP, CNAME www → @. Safe to run before DNS — the redirect waits for it.', inst.customDomain || '');
      if (domain === null) return;
      onAction(inst, 'domain', { domain: domain.trim() });
      return;
    }
    onAction(inst, action);
  };

  const external = inst.delivery === 'external';
  const siteMode = inst.mode === 'site';
  // Site mode: the stack is off, so every stack-touching action is a 409 on
  // the server — offer only the way back (and terminate).
  const siteModeEligible = !external && inst.status === 'live' && inst.envRole !== 'uat' && !!inst.host
    && (!inst.deployment?.target || inst.deployment.target === 'managed')
    && !!inst.template?.appType && inst.template.appType !== 'blank';
  // An external app with a GitHub repo can have its brain pulled by the plane
  // (fleetBrainWorker) — the foundation apps that do not phone home.
  const canBrainPull = external && inst.repo?.provider === 'github' && inst.status === 'live';
  // Zero-downtime updates (blue/green app tier): a split instance swaps,
  // an unsplit live managed one can be split now.
  const split = !!inst.stack?.split;
  const swapping = !!inst.stack?.next?.ports;
  const swapEligible = !external && inst.status === 'live' && !!inst.host && !inst.warmPool && !inst.migrate
    && (!inst.deployment?.target || inst.deployment.target === 'managed');
  const rowActions = external ? [...(canBrainPull ? ['brain-pull'] : []), 'terminate']
    : siteMode ? ['app-mode', 'terminate']
      : [...ACTIONS, 'domain', 'routing', ...(swapEligible && !swapping ? [split ? 'swap' : 'split'] : []), ...(siteModeEligible ? ['site-mode'] : [])];
  const actionLabel = (a) => (external && a === 'terminate' ? 'remove' : a === 'site-mode' ? 'site mode' : a === 'app-mode' ? 'back to app' : a === 'brain-pull' ? (inst.brainPull ? 'pull brain now' : 'pull brain') : a === 'swap' ? 'swap' : a === 'split' ? 'split' : a);
  const actionTitle = (a) => (a === 'routing' ? 'Re-stamp the model gateway key and point the kit at nventr/auto (no rebuild)'
    : a === 'site-mode' ? 'Serve the public site statically from the edge and stop the whole stack (no slot, no host share); data stays on disk'
      : a === 'app-mode' ? 'Start the stack again and route the edge back to it'
        : a === 'swap' ? `Update to the kit ref without downtime: stage the ${inst.stack?.color === 'green' ? 'blue' : 'green'} app tier on a free port against the same data, verify it off-edge, flip the edge, retire ${inst.stack?.color || 'the old tier'}`
          : a === 'split' ? 'First zero-downtime update: convert this monolith to a blue/green app tier on one data tier (the running stack becomes blue; its backend+caddy retire after the flip)'
            : a === 'brain-pull' ? `Read brain/ and stack facts from ${inst.repo?.name} now and keep pulling every few hours (Fleet brains, NoVA)` : undefined);
  // What stack the app is (appInstance.profile): kit instances are written
  // at go-live, foundation apps are detected from their repo.
  const profile = inst.profile;
  const profileChip = profile?.kind
    ? `${profile.dataPlane || '?'} · ${profile.backend === 'ecs' ? 'fargate' : profile.backend || '?'}`
    : external ? 'external' : null;
  const profileTitle = profile?.kind
    ? `${profile.kind} app — data plane ${profile.dataPlane || '?'}, hosting ${profile.hosting || '?'}, backend ${profile.backend || '?'}${profile.phoneHome ? ', phones home' : ', no phone-home'}${profile.inAppBrain ? ', in-app brain' : ''}${profile.foundationImportedAt ? `, foundation imported ${profile.foundationImportedAt}` : ''} (detected from ${profile.detectedFrom} ${fmtDate(profile.detectedAt)})`
    : 'externally hosted — stack not detected yet (pull brain to detect it)';

  return (
    <>
      <tr className="aa-row" onClick={() => setOpen((v) => !v)}>
        <td>
          {inst.name}
          {siteMode ? <span className="aa-tag" title={`site mode since ${fmtDate(inst.siteMode?.since)} — static public site only, stack stopped`}>site</span> : null}
          {swapping ? <span className="aa-tag" title={`swap in flight — ${inst.stack.next.color} app tier staging on :${inst.stack.next.ports.http} for ${inst.stack.next.ref || 'the kit ref'} since ${fmtDate(inst.stack.next.at)}`}>swapping</span>
            : split ? <span className="aa-tag" title={`blue/green app tier — ${inst.stack.color || '?'} live on :${inst.stack.ports?.http}${inst.stack.lastSwap ? `; last swap ${inst.stack.lastSwap.ref || ''} ${fmtDate(inst.stack.lastSwap.at)}, edge gap ${inst.stack.lastSwap.gapMs ?? '?'} ms${inst.stack.lastSwap.flippedBack ? ' (flipped back)' : ''}` : ''}`}>{inst.stack.color || 'split'}</span> : null}
          {profileChip ? <span className="aa-tag" title={profileTitle}>{profileChip}</span> : null}
          {inst.billing === 'internal' && !external ? <span className="aa-tag">internal</span> : null}
          {inst.envRole === 'uat' ? <span className="aa-tag">UAT</span> : null}
          {inst.uatAppId ? <span className="aa-tag" title={`UAT app ${inst.uatAppId}`}>has UAT</span> : null}
          {Object.entries(inst.template?.addons || {}).filter(([, v]) => v === true).map(([k]) => (
            <span key={k} className="aa-tag aa-tag-addon" title={`${k} add-on`}>+{k}</span>
          ))}
        </td>
        <td className="aa-mono">{inst.slug}{inst.customDomain ? <div className="cs-help" title="custom domain">{inst.customDomain}</div> : null}</td>
        <td>{inst.plan}</td>
        <td><StatusBadge status={inst.status} /></td>
        <td>{inst.deployment?.target && inst.deployment.target !== 'managed' ? <span className="aa-tag">{inst.deployment.target}</span> : 'managed'}</td>
        <td><LicenseChip license={inst.license} /></td>
        <td className="aa-mono">{external ? '—' : (inst.host || '—')}</td>
        <td>{inst.billing === 'internal' ? 'n/a' : fmtDate(inst.prepaidUntil)}</td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="aa-actions" data-agent-guard>
            {rowActions.map((a) => (
              <button
                key={a}
                type="button"
                className="cs-btn cs-btn-sm cs-btn-outline"
                disabled={busy}
                title={actionTitle(a)}
                onClick={() => act(a)}
                data-agent-noclick={a === 'site-mode' || a === 'app-mode' ? true : undefined}
              >
                {actionLabel(a)}
              </button>
            ))}
          </div>
        </td>
      </tr>
      {confirming && (
        <tr className="aa-detail-row">
          <td colSpan={9} onClick={(e) => e.stopPropagation()}>
            <div className="aa-confirm" data-agent-guard>
              <span>
                {external ? (
                  <>Removing <strong>{inst.slug}</strong> unregisters it from the fleet (the app itself is untouched). Type the slug to confirm:</>
                ) : (
                  <>Terminating <strong>{inst.slug}</strong> tears the stack down and deletes its data. Type the slug to confirm:</>
                )}
              </span>
              <input
                className="cs-input"
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                placeholder={inst.slug}
              />
              <button
                type="button"
                className="cs-btn cs-btn-sm"
                disabled={confirmSlug !== inst.slug || busy}
                onClick={() => { onAction(inst, 'terminate'); setConfirming(false); setConfirmSlug(''); }}
              >
                {external ? 'Remove' : 'Terminate'}
              </button>
              <button
                type="button"
                className="cs-btn cs-btn-sm cs-btn-ghost"
                onClick={() => { setConfirming(false); setConfirmSlug(''); }}
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
      {open && (
        <tr className="aa-detail-row">
          <td colSpan={9}>
            {inst.statusDetail ? <p className="aa-status-detail">{inst.statusDetail}</p> : null}
            {!external && <StageList job={job} />}
            {!external && (
              <p className="cs-help" onClick={(e) => e.stopPropagation()}>
                Starter site:{' '}
                <select
                  className="cs-input"
                  style={{ width: 'auto', display: 'inline-block' }}
                  value={appType}
                  onChange={(e) => setAppType(e.target.value)}
                >
                  {APP_TYPES.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
                {' '}
                <button
                  type="button"
                  className="cs-btn cs-btn-sm cs-btn-outline"
                  disabled={busy || appType === (inst.template?.appType || 'blank')}
                  onClick={saveAppType}
                >
                  Save type
                </button>
                {appTypeMsg && <span> · {appTypeMsg}</span>}
              </p>
            )}
            {inst.licenseId && inst.license ? (
              <p className="cs-help">
                License: {inst.license.status}
                {inst.license.termEnd ? ` · term ends ${fmtDate(inst.license.termEnd)}` : ''}
                {inst.license.lastSeenAt ? ` · last check ${fmtDate(inst.license.lastSeenAt)}` : ' · never phoned home'}
                {inst.license.status === 'active' && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-outline"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Revoke the license for ${inst.slug}? The instance hard-stops within 12h of its next validation.`)) {
                          onRevokeLicense(inst);
                        }
                      }}
                    >
                      Revoke license
                    </button>
                  </>
                )}
              </p>
            ) : null}
            <p className="cs-help">
              Org: <Link to={`/orgs/${inst.orgId}/apps/${inst.appId}`}>{inst.orgId}</Link>
              {inst.urls?.app ? (
                <>
                  {' · '}
                  <a href={inst.urls.app} target="_blank" rel="noreferrer">{inst.urls.app}</a>
                </>
              ) : null}
              {inst.repo?.url ? (
                <>
                  {' · '}
                  <a href={inst.repo.url} target="_blank" rel="noreferrer">{inst.repo.name}</a>
                </>
              ) : null}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Health board — renders the monitor block api/monitorWorker.js maintains on
// each instance, plus the incident feed.

function healthState(inst) {
  if (inst.status !== 'live') return 'paused';
  return inst.monitor?.state || 'unknown';
}

// The kit's feature self-test (monitor.selfTest, phoned home with the
// health report): passing / failing / no report (older kit). Pending =
// designed screens without a built route yet — not a failure.
function featureState(m) {
  const st = m?.selfTest;
  if (!st) return 'unknown';
  return st.ok ? 'up' : 'down';
}

// Dev Console health across the fleet — what each instance phoned home
// (monitor.devReliability / ship / git / gitGateway, kits since 2026-10-30):
// errors by code over 24 h, runs stuck past the stale window, ships waiting
// or stalled, and the CodeCommit throttle picture per instance. The
// fleet.dev-console journey alerts on the same numbers.
const FRESH_MS = 30 * 60e3;
function DevConsoleHealth({ instances }) {
  const rows = useMemo(() => (instances || [])
    .map((i) => ({ inst: i, r: i.monitor?.devReliability || null, ship: i.monitor?.ship || null, git: i.monitor?.git || null, gw: i.monitor?.gitGateway || null }))
    .filter((x) => x.r || x.ship || x.git)
    .sort((a, b) => ((b.r?.stuck?.sessions || 0) + (b.r?.stuck?.jobs || 0) + (b.git?.exhausted || 0)) - ((a.r?.stuck?.sessions || 0) + (a.r?.stuck?.jobs || 0) + (a.git?.exhausted || 0))), [instances]);
  const totals = useMemo(() => {
    const t = { reporting: 0, stuck: 0, failed: 0, exhausted: 0, needsUser: 0, stalled: 0, codes: {}, sloJudged: 0, sloBreaching: 0, sloBy: {} };
    for (const x of rows) {
      if (x.r && Date.now() - (x.r.at || 0) < FRESH_MS) {
        t.reporting += 1;
        if (x.r.slo) {
          t.sloJudged += 1;
          if (!x.r.slo.ok) t.sloBreaching += 1;
          for (const s of x.r.slo.results || []) if (!s.ok && !s.na) t.sloBy[s.id] = (t.sloBy[s.id] || 0) + 1;
        }
        t.stuck += (x.r.stuck?.sessions || 0) + (x.r.stuck?.jobs || 0);
        t.failed += (x.r.sessions?.failed || 0) + (x.r.jobs?.failed || 0);
        for (const [c, n] of Object.entries(x.r.errorsByCode || {})) t.codes[c] = (t.codes[c] || 0) + n;
      }
      if (x.git) t.exhausted += x.git.exhausted || 0;
      if (x.ship) { t.needsUser += x.ship.needsUser || 0; t.stalled += x.ship.stalled || 0; }
    }
    return t;
  }, [rows]);
  const topCodes = Object.entries(totals.codes).sort((a, b) => b[1] - a[1]).slice(0, 8);
  if (!rows.length) return null;
  const fmtCodes = (codes) => Object.entries(codes || {}).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([c, n]) => `${c} ×${n}`).join(', ');
  return (
    <>
      <h2 style={{ marginTop: 24 }}>Dev Console health</h2>
      <div className="aa-health-strip" data-nova-note="Dev Console reliability across live instances over the last 24 h: instances reporting, runs stuck past their stale window (should be zero), failed sessions and deploy jobs, CodeCommit calls that exhausted their retries, ships waiting on a person or stalled, and the error codes seen most. The fleet.dev-console journey alerts on stuck runs, error spikes and hot shards.">
        <div className="aa-health-stat"><strong>{totals.reporting}</strong> reporting</div>
        <div className={`aa-health-stat ${totals.stuck ? 'is-err' : 'is-ok'}`} title="Runs past the stale window without a terminal status (lifecycle sweep should close them)"><strong>{totals.stuck}</strong> stuck</div>
        <div className={`aa-health-stat ${totals.failed ? '' : 'is-ok'}`}><strong>{totals.failed}</strong> failed / 24 h</div>
        <div className={`aa-health-stat ${totals.exhausted ? 'is-err' : 'is-ok'}`} title="CodeCommit calls that gave up after the full retry ladder since boot"><strong>{totals.exhausted}</strong> git exhausted</div>
        <div className={`aa-health-stat ${totals.stalled ? 'is-err' : ''}`}><strong>{totals.needsUser}</strong> ships waiting · <strong>{totals.stalled}</strong> stalled</div>
        {topCodes.length ? <div className="aa-health-stat cs-help" title="Error codes across the fleet, last 24 h">{topCodes.map(([c, n]) => `${c} ×${n}`).join(' · ')}</div> : null}
        {totals.sloJudged ? (
          <div className={`aa-health-stat ${totals.sloBreaching ? 'is-err' : 'is-ok'}`} title="Instances whose own SLO verdicts (session start p95 < 60 s, stuck = 0, ship success ≥ 99 %, CodeCommit exhausted = 0; kit dev.slo) are all green">
            <strong>{totals.sloJudged - totals.sloBreaching}</strong>/{totals.sloJudged} SLO green{Object.keys(totals.sloBy).length ? <span className="cs-help"> · {Object.entries(totals.sloBy).map(([id, n]) => `${id} ×${n}`).join(', ')}</span> : null}
          </div>
        ) : null}
      </div>
      <div className="cs-card aa-tablewrap" data-nova-note="One row per instance: the four SLO verdicts the instance computed (✓ met, ✗ breached, ○ too few samples; hover for the numbers), sessions finished with a PR over total, failed sessions and jobs, stuck runs, the top error codes, ship state, git throttle counters (sends / throttled / exhausted) and the gateway's cooldown state; a column in red is the number to chase.">
        <table className="aa-table">
          <thead>
            <tr><th>App</th><th>SLO</th><th>Sessions 24 h</th><th>Failed</th><th>Stuck</th><th>Top errors</th><th>Ship</th><th>Git (sent / throttled / exhausted)</th><th>Gateway</th><th>Reported</th></tr>
          </thead>
          <tbody>
            {rows.map(({ inst, r, ship, git, gw }) => {
              const stuck = (r?.stuck?.sessions || 0) + (r?.stuck?.jobs || 0);
              const failed = (r?.sessions?.failed || 0) + (r?.jobs?.failed || 0);
              const stale = r && Date.now() - (r.at || 0) > FRESH_MS;
              return (
                <tr key={`${inst.orgId}/${inst.appId}`} className={stale ? 'cs-help' : ''}>
                  <td><Link to={`/orgs/${inst.orgId}/apps/${inst.appId}`}>{inst.name}</Link> <span className="aa-mono cs-help">{inst.slug}</span></td>
                  <td>{r?.slo ? (
                    <span title={(r.slo.results || []).map((s) => `${s.na ? '○' : s.ok ? '✓' : '✗'} ${s.id}: ${s.text}`).join('\n')}>
                      {(r.slo.results || []).map((s) => <span key={s.id} className={`aa-health-badge is-${s.na ? 'unknown' : s.ok ? 'up' : 'down'}`} style={{ marginRight: 2 }}>{s.na ? '○' : s.ok ? '✓' : '✗'}</span>)}
                    </span>
                  ) : <span className="cs-help">older kit</span>}</td>
                  <td>{r ? <>{r.sessions?.withPr || 0} PR / {r.sessions?.total || 0}{r.sessions?.startP95Ms != null ? <span className="cs-help"> · start p95 {Math.round(r.sessions.startP95Ms / 1000)} s</span> : null}</> : '—'}</td>
                  <td className={failed ? 'aa-warn-text' : ''}>{r ? failed : '—'}</td>
                  <td>{r ? <span className={`aa-health-badge is-${stuck ? 'down' : 'up'}`}>{stuck}</span> : '—'}</td>
                  <td className="aa-status-detail">{r ? (fmtCodes(r.errorsByCode) || <span className="cs-help">none</span>) : '—'}</td>
                  <td>{ship ? <>{ship.needsUser ? `${ship.needsUser} waiting` : ''}{ship.running ? ` ${ship.running} deploying` : ''}{ship.stalled ? <span className="aa-warn-text"> {ship.stalled} stalled</span> : ''}{ship.blocked ? ` ${ship.blocked} blocked` : ''}{!ship.needsUser && !ship.running && !ship.stalled && !ship.blocked ? <span className="cs-help">idle</span> : null}</> : '—'}</td>
                  <td className={git?.exhausted ? 'aa-warn-text' : ''}>{git ? `${git.sends} / ${git.throttled} / ${git.exhausted}` : '—'}</td>
                  <td>{gw ? <span title={`calls ${gw.calls} · throttled ${gw.throttled} · cooldowns ${gw.cooldowns} · refused ${gw.refused}`}>{gw.coolingDown ? <span className="aa-warn-text">cooling down</span> : 'ok'}{gw.budgetRemaining != null ? <span className="cs-help"> · {gw.budgetRemaining}/{gw.budgetPerMinute} budget</span> : null}{gw.refused ? <span className="cs-help"> · {gw.refused} refused</span> : null}</span> : <span className="cs-help">older kit</span>}</td>
                  <td>{fmtAgo(r?.at || ship?.at || git?.at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HealthTab({ instances, incidents }) {
  const live = (instances || []).filter((i) => i.status === 'live');
  const [openFeatures, setOpenFeatures] = useState(null);
  const counts = useMemo(() => {
    const c = { up: 0, down: 0, unknown: 0, featuresFailing: 0, featuresReporting: 0 };
    live.forEach((i) => {
      c[healthState(i)] = (c[healthState(i)] || 0) + 1;
      if (i.monitor?.selfTest) { c.featuresReporting += 1; if (!i.monitor.selfTest.ok) c.featuresFailing += 1; }
    });
    return c;
  }, [live]);
  const openIncidents = (incidents || []).filter((i) => i.status === 'open');

  return (
    <>
      <div className="aa-health-strip">
        <div className="aa-health-stat is-ok"><strong>{counts.up}</strong> up</div>
        <div className={`aa-health-stat ${counts.down ? 'is-err' : ''}`}><strong>{counts.down}</strong> down</div>
        <div className="aa-health-stat"><strong>{counts.unknown || 0}</strong> unknown</div>
        <div className={`aa-health-stat ${openIncidents.length ? 'is-err' : ''}`}>
          <strong>{openIncidents.length}</strong> open incident{openIncidents.length === 1 ? '' : 's'}
        </div>
        <div className={`aa-health-stat ${counts.featuresFailing ? 'is-err' : counts.featuresReporting ? 'is-ok' : ''}`} title="Kit feature self-test: instances whose latest pass is green / reporting">
          <strong>{counts.featuresReporting - counts.featuresFailing}/{counts.featuresReporting}</strong> features passing
        </div>
      </div>

      <div className="cs-card aa-tablewrap">
        <table className="aa-table">
          <thead>
            <tr>
              <th>App</th><th>State</th><th>Features</th><th>Latency</th><th>TLS expires</th>
              <th>Kit version</th><th>Last check</th>
            </tr>
          </thead>
          <tbody>
            {live.map((inst) => {
              const m = inst.monitor || {};
              const state = healthState(inst);
              const st = m.selfTest;
              const key = `${inst.orgId}/${inst.appId}`;
              return (
                <React.Fragment key={key}>
                <tr>
                  <td>
                    <Link to={`/orgs/${inst.orgId}/apps/${inst.appId}`}>{inst.name}</Link>{' '}
                    <span className="aa-mono cs-help">{inst.slug}</span>
                  </td>
                  <td>
                    <span className={`aa-health-badge is-${state}`}>
                      {state}{state === 'down' && m.consecFails ? ` ×${m.consecFails}` : ''}
                    </span>
                  </td>
                  <td>
                    {st ? (
                      <button type="button" className="aa-linkbtn" onClick={() => setOpenFeatures(openFeatures === key ? null : key)} title={`self-test ${fmtAgo(st.at)}${m.kitRef ? ` · ${m.kitRef}` : ''}`}>
                        <span className={`aa-health-badge is-${featureState(m)}`}>{st.passed}/{st.total}</span>
                        {st.pending ? <span className="cs-help"> · {st.pending} pending</span> : null}
                      </button>
                    ) : <span className="cs-help">—</span>}
                  </td>
                  <td>{m.lastLatencyMs != null ? `${m.lastLatencyMs} ms` : '—'}</td>
                  <td className={m.tlsDaysLeft != null && m.tlsDaysLeft < 14 ? 'aa-warn-text' : ''}>
                    {m.tlsDaysLeft != null ? `${m.tlsDaysLeft} d` : '—'}
                  </td>
                  <td className={m.versionBehind || m.runtimeBehind ? 'aa-warn-text' : ''}>
                    {m.versionBehind ? 'behind' : 'current'}{m.kitRef ? <span className="cs-help aa-mono"> {m.kitRef}</span> : null}
                    {m.runtimeBehind ? <span title="Node below apps.monitor.minNodeVersion — the next kit update rebuilds the image"> · runtime behind</span> : null}
                    {m.node ? <span className="cs-help aa-mono"> {m.node}</span> : null}
                  </td>
                  <td>{fmtAgo(m.lastCheckAt)}</td>
                </tr>
                {openFeatures === key && st && (
                  <tr className="aa-features-row">
                    <td colSpan={7}>
                      <div className="aa-features-detail" data-nova-note="Kit feature self-test drilldown: failing case ids with the error, suite composition, and designed screens still pending a built route.">
                        <div>
                          <strong>{st.ok ? 'Passing' : 'Failing'}</strong> · {st.passed} pass · {st.failed} fail · {st.skipped} skipped · {st.pending} design pending · {fmtAgo(st.at)}
                          {st.suite ? <span className="cs-help"> · suite: {Object.entries(st.suite).map(([k, v]) => `${k} ${v}`).join(', ')}</span> : null}
                          {st.design?.screens ? <span className="cs-help"> · design {st.design.built}/{st.design.screens} screens built</span> : null}
                        </div>
                        {(st.failing || []).map((f) => (
                          <div key={f.id} className="aa-status-detail"><span className="aa-mono">{f.id}</span> — {f.error}</div>
                        ))}
                        {inst.urls?.dev ? <a href={inst.urls.dev} target="_blank" rel="noreferrer" className="cs-help">Open the instance Dev Console → Self-test for per-case results ↗</a> : null}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {!live.length && (
              <tr><td colSpan={7} className="cs-help">No live instances to monitor.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <DevConsoleHealth instances={live} />

      <h2 style={{ marginTop: 24 }}>Incidents</h2>
      <div className="cs-card aa-tablewrap">
        <table className="aa-table">
          <thead>
            <tr><th>App</th><th>Status</th><th>Opened</th><th>Duration</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {(incidents || []).map((inc) => (
              <tr key={inc.id} className={inc.status === 'open' ? 'aa-incident-open' : ''}>
                <td className="aa-mono">{inc.slug}{inc.kind === 'feature' ? <span className="aa-health-badge is-down" style={{ marginLeft: 6 }}>features</span> : null}</td>
                <td>{inc.status}</td>
                <td>{new Date(inc.openedAt).toLocaleString()}</td>
                <td>
                  {inc.status === 'open'
                    ? fmtAgo(inc.openedAt).replace(' ago', '')
                    : inc.durationMs != null ? `${Math.round(inc.durationMs / 60000)}m` : '—'}
                </td>
                <td className="aa-status-detail">{inc.reason || '—'}</td>
              </tr>
            ))}
            {incidents && !incidents.length && (
              <tr><td colSpan={5} className="cs-help">No incidents recorded.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Costs — actual AWS spend per app (instanceCostWorker → Cost Explorer by
// kit:slug tag). Burn/day from the last complete daily datapoints.

const fmtUsd2 = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

function topService(byService) {
  const entries = Object.entries(byService || {}).sort((a, b) => b[1] - a[1]);
  return entries.length ? `${entries[0][0]} (${fmtUsd2(entries[0][1])})` : '—';
}

const GAP_LABEL = {
  zombie: 'Terminated, still billing',
  'unknown-slug': 'kit:slug with no app',
  'unknown-cluster': 'Cluster nobody owns',
  'unknown-project': 'project tag that is not a plane',
  coverage: 'Account below coverage floor',
  'tag-inactive': 'Tag key has no data yet',
  'ce-stale': 'Cost Explorer pull stale',
};

// The whole AWS bill (every payer) against what the fleet can name —
// shared/costReconcile.js on the backend; this only draws it.
function LedgerPanel({ ledger, period }) {
  const [openAccount, setOpenAccount] = useState(null);
  if (!ledger) return null;
  const home = (ledger.payers || []).find((p) => p.home);
  const lowHome = home?.coveragePct != null && home.coveragePct < ledger.minCoveragePct;
  return (
    <>
      <div className="aa-health-strip" data-nova-note="Whole AWS bill across every payer account the platform reads, month to date, against what is attributed to apps, planes and named buckets; the unattributed remainder is the audit's open balance">
        <div className="aa-health-stat"><strong>{fmtUsd2(ledger.totalUsd)}</strong> all AWS MTD <span className="cs-help">({ledger.counts?.payers} payer{ledger.counts?.payers === 1 ? '' : 's'} · {ledger.counts?.accounts} account{ledger.counts?.accounts === 1 ? '' : 's'})</span></div>
        <div className="aa-health-stat"><strong>{fmtUsd2(ledger.burnPerDayUsd)}</strong> burn / day</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(ledger.projectedUsd)}</strong> projected · {period}</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(ledger.attributedUsd)}</strong> attributed <span className="cs-help">(apps {fmtUsd2(ledger.attributed?.apps)} · planes {fmtUsd2(ledger.attributed?.planes)}{ledger.attributed?.hosts ? ` incl. hosts ${fmtUsd2(ledger.attributed.hosts)}` : ''} · buckets {fmtUsd2(ledger.attributed?.buckets)})</span></div>
        <div className={`aa-health-stat ${ledger.unattributedUsd > 0 && lowHome ? 'is-err' : ''}`}><strong>{fmtUsd2(ledger.unattributedUsd)}</strong> unattributed <span className="cs-help">({ledger.coveragePct == null ? '—' : `${ledger.coveragePct}% covered`}{home ? ` · home payer ${home.coveragePct == null ? '—' : `${home.coveragePct}%`}, floor ${ledger.minCoveragePct}%` : ''})</span></div>
      </div>
      {(ledger.buckets || []).length > 0 && (
        <p className="cs-help" style={{ marginTop: 6 }}>
          Buckets: {ledger.buckets.map((b) => `${b.label} ${fmtUsd2(b.usd)}`).join(' · ')}
        </p>
      )}
      <div className="cs-card aa-tablewrap" style={{ marginTop: 12 }} data-nova-note="One row per AWS account under each payer: total, attributed, unattributed, coverage, burn, top services; a row opens its tag breakdown">
        <table className="aa-table">
          <thead><tr><th>Payer / account</th><th>MTD</th><th>Attributed</th><th>Unattributed</th><th>Coverage</th><th>Burn / day</th><th>Top services</th><th>Pulled</th></tr></thead>
          <tbody>
            {(ledger.payers || []).map((p) => (
              <Fragment key={p.payer}>
                <tr style={{ fontWeight: 600 }}>
                  <td>{p.payerName} <span className="aa-mono cs-help">{p.payer}</span>{p.home && <span className="ap-badge is-muted" style={{ marginLeft: 6 }}>home</span>}</td>
                  <td>{fmtUsd2(p.totalUsd)}</td>
                  <td>{fmtUsd2(p.attributedUsd)}</td>
                  <td>{fmtUsd2(p.unattributedUsd)}</td>
                  <td>{p.coveragePct == null ? '—' : `${p.coveragePct}%`}</td>
                  <td>{fmtUsd2(p.burnPerDayUsd)}</td>
                  <td className="cs-help">{p.error ? <span className="ap-badge is-err">{p.error}</span> : `${p.ceCalls || 0} CE calls`}</td>
                  <td className="cs-help">{p.pulledAt ? new Date(p.pulledAt).toLocaleString() : '—'}</td>
                </tr>
                {(ledger.perAccount || []).filter((a) => a.payer === p.payer).map((a) => (
                  <tr key={`${p.payer}/${a.id}`} className="aa-row" onClick={() => setOpenAccount(openAccount === a.id ? null : a.id)} style={{ cursor: 'pointer' }}>
                    <td style={{ paddingLeft: 24 }}>{a.label} <span className="aa-mono cs-help">{a.id}</span></td>
                    <td>{fmtUsd2(a.totalUsd)}</td>
                    <td>{fmtUsd2(a.attributedUsd)} <span className="cs-help">apps {fmtUsd2(a.apps)} · planes {fmtUsd2(a.planes)} · buckets {fmtUsd2(a.buckets)}</span></td>
                    <td style={{ color: a.unattributedUsd > 0 && a.coveragePct != null && a.coveragePct < ledger.minCoveragePct ? 'var(--danger)' : undefined }}>{fmtUsd2(a.unattributedUsd)}</td>
                    <td>{a.coveragePct == null ? '—' : <span className={`ap-badge ${a.coveragePct >= ledger.minCoveragePct ? 'is-ok' : 'is-warn'}`}>{a.coveragePct}%</span>}</td>
                    <td>{fmtUsd2(a.burnPerDayUsd)}</td>
                    <td className="cs-help">{(a.topServices || []).map((s) => `${s.service} ${fmtUsd2(s.usd)}`).join(' · ') || '—'}</td>
                    <td className="cs-help">{openAccount === a.id ? 'hide tags' : 'tags →'}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
            {!(ledger.payers || []).length && <tr><td colSpan={8} className="cs-help">No payer pulled yet — the cloud-cost worker runs daily at 06:30 UTC.</td></tr>}
          </tbody>
        </table>
      </div>
      {openAccount && <AccountTags ledger={ledger} accountId={openAccount} />}
      <div className="cs-card aa-tablewrap" style={{ marginTop: 12 }} data-nova-note="Gaps: the live audit — clusters nobody owns, terminated apps whose AWS resources still bill, project tags that are not a plane, accounts under the coverage floor, cost-allocation keys without data yet, stale pulls; each names the fix a person runs. Nothing here deletes anything.">
        <div className="cs-help" style={{ padding: '8px 10px 0' }}>
          Gaps · {ledger.counts?.gaps || 0} open{ledger.counts?.zombies ? ` · ${ledger.counts.zombies} zombie${ledger.counts.zombies === 1 ? '' : 's'}` : ''} — what the ledger cannot name, with the fix. No button here changes AWS.
        </div>
        <table className="aa-table">
          <thead><tr><th>Kind</th><th>Where</th><th>What</th><th>MTD</th><th>Fix</th></tr></thead>
          <tbody>
            {(ledger.gaps || []).map((g, i) => (
              <tr key={`${g.kind}-${g.payer}-${g.account || ''}-${g.key || ''}-${g.value || i}`}>
                <td><span className={`ap-badge ${g.kind === 'zombie' ? 'is-err' : g.kind === 'ce-stale' || g.kind === 'coverage' ? 'is-warn' : 'is-muted'}`}>{GAP_LABEL[g.kind] || g.kind}</span></td>
                <td className="cs-help">{g.accountLabel || g.payerName}{g.key ? <> · <span className="aa-mono">{g.key}</span></> : null}</td>
                <td>
                  {g.value && <span className="aa-mono">{g.value}</span>}
                  {g.app?.orgId && g.app?.appId && <> · <Link to={`/orgs/${g.app.orgId}/apps/${g.app.appId}`}>{g.app.name || g.app.slug}</Link></>}
                  <div className="cs-help">{g.detail}</div>
                </td>
                <td>{g.usd ? fmtUsd2(g.usd) : '—'}</td>
                <td className="cs-help">{g.fix}</td>
              </tr>
            ))}
            {!(ledger.gaps || []).length && <tr><td colSpan={5} className="cs-help">No gaps — every dollar is an app, a plane or a bucket.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Tag breakdown for one account — raw byAccountTag is not sent to the client;
// the ledger's perAccount row carries the per-kind totals, so this shows the
// gap rows and bucket claims that touch the account.
function AccountTags({ ledger, accountId }) {
  const rows = (ledger.gaps || []).filter((g) => g.account === accountId && g.value);
  const acct = (ledger.perAccount || []).find((a) => a.id === accountId);
  return (
    <div className="cs-card" style={{ marginTop: 8, padding: '8px 10px' }}>
      <div className="cs-help">
        {acct?.label} · apps {fmtUsd2(acct?.apps)} · planes {fmtUsd2(acct?.planes)} · buckets {fmtUsd2(acct?.buckets)} · unattributed {fmtUsd2(acct?.unattributedUsd)}
        {rows.length ? ` · ${rows.length} tagged value${rows.length === 1 ? '' : 's'} nobody claims: ${rows.map((g) => `${g.value} ${fmtUsd2(g.usd)}`).join(' · ')}` : ' · every tagged value in this account is claimed'}
      </div>
    </div>
  );
}

const fmtTok = (v) => (v == null ? '—' : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : String(Math.round(v)));

// Dev Console cost vs the Cursor account (GET /apps/admin/cursor-usage):
// what instances say their routed work cost (monitor.dev.cost, by tier)
// beside what the team account was actually charged (cursorUsage/{month},
// pulled nightly once a Cursor Admin API key is set). Until the key
// exists the right-hand side says so instead of guessing.
function DevCostPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (run) => {
    setBusy(true);
    try { setD(await apiGet(`/apps/admin/cursor-usage${run ? '?run=1' : ''}`)); } catch { setD(null); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);
  if (!d) return null;
  const f = d.fleet || { byTier: {}, perInstance: [] };
  const a = d.account;
  const tiers = Object.entries(f.byTier || {}).sort((x, y) => y[1].usd - x[1].usd);
  const top = f.perInstance.slice(0, 6);
  const models = Object.entries(a?.byModel || {}).sort((x, y) => y[1].chargedCents - x[1].chargedCents).slice(0, 6);
  return (
    <div className="aa-grid-3" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
      <div className="cs-card aa-tablewrap" data-nova-note="What instances report their Dev Console runs cost over the last day, by routing tier (bulk / strong / frontier), with the share of runs whose cost the CLI actually reported and the cache-read share of their tokens">
        <div className="cs-help" style={{ padding: '8px 10px 0' }}>Dev Console · fleet, last 24 h · {f.instances} instance{f.instances === 1 ? '' : 's'} · <strong>{fmtUsd2(f.usd)}</strong> over {f.sessions} session{f.sessions === 1 ? '' : 's'}</div>
        <table className="aa-table">
          <thead><tr><th>Tier</th><th>Sessions</th><th>USD</th></tr></thead>
          <tbody>
            {tiers.map(([k, v]) => <tr key={k}><td className="aa-mono">{k}</td><td>{v.sessions}</td><td>{fmtUsd2(v.usd)}</td></tr>)}
            {!tiers.length && <tr><td colSpan={3} className="cs-help">No instance on a kit that reports its cost picture yet (kits since 2026-11-04).</td></tr>}
          </tbody>
        </table>
        {top.length > 0 && (
          <table className="aa-table" style={{ marginTop: 6 }}>
            <thead><tr><th>Instance</th><th>USD</th><th>Reported</th><th>Cache read</th></tr></thead>
            <tbody>{top.map((r) => <tr key={r.slug}><td className="aa-mono">{r.slug}</td><td>{fmtUsd2(r.usd)}</td><td className="cs-help">{r.reportedPct == null ? '—' : `${r.reportedPct}%`}</td><td className="cs-help">{r.cacheReadPct == null ? '—' : `${r.cacheReadPct}%`}</td></tr>)}</tbody>
          </table>
        )}
      </div>
      <div className="cs-card aa-tablewrap" data-nova-note="What the Cursor team account was charged this month, by model, from the Admin API; the cloud-agent slice is the part that ran as Dev Console cloud sessions">
        <div className="cs-help" style={{ padding: '8px 10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Cursor account · {d.month}{a ? <> · <strong>{fmtUsd2(a.chargedUsd)}</strong> charged · cloud agents {fmtUsd2(a.cloudAgents?.usd)} · cache read {a.cacheReadPct == null ? '—' : `${a.cacheReadPct}%`}</> : null}</span>
          {d.keyPresent && <button type="button" className="cs-btn cs-btn-sm" disabled={busy} onClick={() => load(true)}>{busy ? 'Pulling…' : 'Pull now'}</button>}
        </div>
        {!d.keyPresent && <p className="cs-help" style={{ padding: '6px 10px' }}>No Cursor Admin API key yet — add one under Launch checklist (<code>cursor-admin-key</code>) and the nightly reconcile fills this in. Until then the fleet's Dev Console dollars are the kit's own estimate, not the invoice.</p>}
        {d.keyPresent && !a && <p className="cs-help" style={{ padding: '6px 10px' }}>Key present; first pull lands at the next scheduled hour — or Pull now.</p>}
        {a && (
          <table className="aa-table">
            <thead><tr><th>Model</th><th>Events</th><th>USD</th></tr></thead>
            <tbody>{models.map(([k, v]) => <tr key={k}><td className="aa-mono">{k}</td><td>{v.events}</td><td>{fmtUsd2(v.chargedCents / 100)}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Token burn — the hosted model bill as a rate (shared/tokenBurn.js): $/day
// and tokens/day from the cost rollup's day snapshots of modelUsage, by
// route and by org, and tokens/day per kit instance from the usage reports.
function TokenBurnPanel({ tokens, period }) {
  const [showAll, setShowAll] = useState(false);
  if (!tokens?.hosted) return null;
  const h = tokens.hosted;
  const f = tokens.fleet || { instances: [] };
  const days = (h.daily || []).slice(-14);
  const known = days.filter((d) => d.usd != null);
  const maxUsd = Math.max(0.01, ...known.map((d) => d.usd));
  const routes = Object.entries(h.byRoute || {});
  const orgs = Object.entries(h.byOrg || {});
  const topOrgs = showAll ? orgs : orgs.slice(0, 8);
  const unattributedPct = h.mtdUsd > 0 ? Math.round((h.unattributedUsd / h.mtdUsd) * 100) : 0;
  const instRows = showAll ? f.instances : f.instances.slice(0, 10);
  const basisBadge = h.basis === 'daily'
    ? <span className="ap-badge is-ok" style={{ fontSize: 10 }}>3-day mean</span>
    : <span className="ap-badge is-muted" style={{ fontSize: 10 }} title="No day snapshots yet — month-to-date divided by days elapsed">MTD avg</span>;
  return (
    <>
      <div className="aa-health-strip" data-nova-note="Token burn: hosted model spend on the platform's own keys as a rate — dollars and tokens per day from daily snapshots of the model ledger, the month projection, and how much of it no organization is attributed to">
        <div className="aa-health-stat"><strong>{fmtUsd2(h.usdPerDay)}</strong> hosted tokens / day {basisBadge}</div>
        <div className="aa-health-stat"><strong>{fmtTok(h.tokensPerDay)}</strong> tokens / day</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(h.mtdUsd)}</strong> hosted MTD <span className="cs-help">({fmtTok(h.tokens)} tokens)</span></div>
        <div className="aa-health-stat"><strong>{fmtUsd2(h.projectedUsd)}</strong> projected · {period}</div>
        <div className={`aa-health-stat ${unattributedPct > 25 ? 'is-err' : ''}`}><strong>{fmtUsd2(h.unattributedUsd)}</strong> no org <span className="cs-help">({unattributedPct}% of hosted spend has no orgId on the call)</span></div>
        <div className="aa-health-stat"><strong>{fmtTok(f.tokensPerDay)}</strong> kit fleet tokens / day <span className="cs-help">({f.instances.length} instance{f.instances.length === 1 ? '' : 's'} · {fmtTok(f.tokens)} MTD{f.devUsd ? ` · Dev Console ${fmtUsd2(f.devUsd)}` : ''})</span></div>
      </div>
      <div className="aa-grid-3" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <div className="cs-card" style={{ padding: '8px 10px' }} data-nova-note="Hosted spend per day for the last 14 days; a hollow bar is a day with no snapshot delta yet">
          <div className="cs-help">Hosted $ / day · last {days.length || 0} day{days.length === 1 ? '' : 's'}{h.snapshotDays ? ` · ${h.snapshotDays} snapshot${h.snapshotDays === 1 ? '' : 's'}` : ' · no snapshots yet (first one lands on the next hourly rollup)'}</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 64, marginTop: 8 }}>
            {days.map((d) => (
              <div key={d.day} title={`${d.day}: ${d.usd == null ? 'unknown' : `${fmtUsd2(d.usd)} · ${fmtTok(d.tokens)} tokens`}`} style={{ flex: 1, height: `${d.usd == null ? 100 : Math.max(2, (d.usd / maxUsd) * 100)}%`, background: d.usd == null ? 'transparent' : 'var(--accent, #4ade80)', border: d.usd == null ? '1px dashed var(--border, #444)' : 'none', borderRadius: 2, opacity: d.day === days[days.length - 1]?.day ? 0.55 : 0.9 }} />
            ))}
            {!days.length && <span className="cs-help">—</span>}
          </div>
          {days.length > 0 && <div className="cs-help" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}><span>{days[0].day.slice(5)}</span><span>{days[days.length - 1].day.slice(5)} (partial)</span></div>}
        </div>
        <div className="cs-card aa-tablewrap" data-nova-note="Hosted spend by route this month — agentos, voice, lab, gateway, embed — with each route's own dollars per day">
          <table className="aa-table">
            <thead><tr><th>Route</th><th>MTD</th><th>Tokens</th><th>$ / day</th></tr></thead>
            <tbody>
              {routes.map(([k, v]) => <tr key={k}><td className="aa-mono">{k}</td><td>{fmtUsd2(v.usd)}</td><td>{fmtTok(v.tokens)}</td><td>{v.usdPerDay == null ? '—' : fmtUsd2(v.usdPerDay)}</td></tr>)}
              {!routes.length && <tr><td colSpan={4} className="cs-help">No hosted calls this month.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="cs-card aa-tablewrap" data-nova-note="Hosted spend by organization this month, largest first; the probe org is the platform's own synthetic health checks">
          <table className="aa-table">
            <thead><tr><th>Org</th><th>MTD</th><th>Tokens</th><th>$ / day</th></tr></thead>
            <tbody>
              {topOrgs.map(([id, v]) => <tr key={id}><td><Link to={`/orgs/${id}/economics?tab=tokens`}>{v.name || id}</Link></td><td>{fmtUsd2(v.usd)}</td><td>{fmtTok(v.tokens)}</td><td>{v.usdPerDay == null ? '—' : fmtUsd2(v.usdPerDay)}</td></tr>)}
              {h.unattributedUsd > 0 && <tr><td className="cs-help">no org on the call</td><td>{fmtUsd2(h.unattributedUsd)}</td><td className="cs-help">—</td><td className="cs-help">—</td></tr>}
              {!orgs.length && <tr><td colSpan={4} className="cs-help">Nothing attributed yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="cs-card aa-tablewrap" style={{ marginTop: 12 }} data-nova-note="Tokens per day per kit instance from each instance's own usage counters phoned home hourly; an instance with no users and a steady floor is its self-test and scheduled agents">
        <table className="aa-table">
          <thead><tr><th>Instance</th><th>Tokens / day</th><th>MTD</th><th>Via nventr</th><th>Users</th><th>Dev Console</th><th>Reported</th></tr></thead>
          <tbody>
            {instRows.map((r) => (
              <tr key={r.slug}>
                <td className="aa-mono">{r.slug}</td>
                <td>{fmtTok(r.tokensPerDay)} {r.basis !== 'daily' && <span className="ap-badge is-muted" style={{ fontSize: 10 }}>MTD avg</span>}</td>
                <td>{fmtTok(r.tokens)}</td>
                <td className="cs-help">{fmtTok(r.viaNventr)}</td>
                <td>{r.users}</td>
                <td>{r.devUsd ? fmtUsd2(r.devUsd) : '—'}</td>
                <td className="cs-help">{r.reportedAt ? new Date(r.reportedAt).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {!f.instances.length && <tr><td colSpan={7} className="cs-help">No instance has phoned home this month.</td></tr>}
          </tbody>
        </table>
        {(f.instances.length > 10 || orgs.length > 8) && (
          <div style={{ padding: '6px 10px' }}><button type="button" className="cs-btn cs-btn-sm" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show fewer' : `Show all (${f.instances.length} instances · ${orgs.length} orgs)`}</button></div>
        )}
      </div>
    </>
  );
}

function CostsTab({ instances, costs, period, plane, planes, businessUsd, hosts, wallets, ledger, tokens, onAction, busy }) {
  const planeRows = Object.entries(planes || {}).filter(([, p]) => p);
  const walletRows = (wallets || []).filter((w) => !w.unlimited);
  const walletTotals = walletRows.reduce((a, w) => ({ spent: a.spent + (w.spentUsd || 0), included: a.included + (w.includedUsd || 0), balance: a.balance + (w.balanceUsd || 0), debited: a.debited + (w.debitedUsd || 0) }), { spent: 0, included: 0, balance: 0, debited: 0 });
  const rows = (instances || [])
    .map((inst) => ({ inst, c: costs?.[inst.slug] }))
    .sort((a, b) => (b.c?.mtdUsd || 0) - (a.c?.mtdUsd || 0));
  const totals = rows.reduce((acc, { c }) => ({
    mtd: acc.mtd + (c?.mtdUsd || 0),
    burn: acc.burn + (c?.burnPerDayUsd || 0),
    proj: acc.proj + (c?.projectedUsd || 0),
    rev: acc.rev + (c?.revenueUsd || 0),
  }), { mtd: 0, burn: 0, proj: 0, rev: 0 });
  const anyData = rows.some(({ c }) => c);
  const idle = rows.filter(({ c }) => c?.idle || c?.neverActive);
  const hostRows = Object.entries(hosts || {}).sort(([a], [b]) => (a < b ? -1 : 1));
  const planeServices = Object.entries(plane?.byService || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <>
      <TokenBurnPanel tokens={tokens} period={period} />
      <DevCostPanel />
      <div style={tokens?.hosted ? { marginTop: 16 } : undefined}><LedgerPanel ledger={ledger} period={period} /></div>
      <div className="aa-health-strip" style={ledger ? { marginTop: 16 } : undefined} data-nova-note="The control plane's own spend (tag project=<plane>) per plane, plus what the fleet attributes to apps and the margin against base fees">
        {planeRows.length > 1 && (
          <div className="aa-health-stat"><strong>{fmtUsd2(businessUsd)}</strong> all planes MTD <span className="cs-help">({planeRows.map(([slug, p]) => `${slug} ${fmtUsd2(p.totalUsd)}`).join(' · ')})</span></div>
        )}
        <div className="aa-health-stat"><strong>{fmtUsd2(plane?.totalUsd)}</strong> plane total MTD <span className="cs-help">(AWS {fmtUsd2(plane?.awsUsd)} actual{plane?.gcp ? ` + Firebase/GCP ${fmtUsd2(plane.gcpUsd)} actual` : ' · Firebase/GCP not connected'})</span></div>
        <div className="aa-health-stat"><strong>{fmtUsd2(plane?.burnPerDayUsd)}</strong> plane burn / day</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(plane?.projectedUsd)}</strong> plane projected · {period}</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(totals.mtd)}</strong> attributed to apps</div>
        <div className="aa-health-stat"><strong>{fmtUsd2(totals.rev - totals.mtd)}</strong> app margin MTD <span className="cs-help">({fmtUsd2(totals.rev)} base fees − cost)</span></div>
        {plane && plane.totalUsd > totals.mtd && (
          <div className="aa-health-stat"><strong>{fmtUsd2(plane.totalUsd - totals.mtd)}</strong> plane overhead <span className="cs-help">(control plane, pool, unattributed)</span></div>
        )}
      </div>
      {planeServices.length > 0 && (
        <p className="cs-help" style={{ marginTop: 6 }}>
          Plane by service: {planeServices.map(([svc, usd]) => `${svc} ${fmtUsd2(usd)}`).join(' · ')}
          {plane?.pulledAt ? ` · pulled ${new Date(plane.pulledAt).toLocaleString()}` : ''}
        </p>
      )}
      {plane?.gcp && Object.keys(plane.gcp.byService || {}).length > 0 && (
        <p className="cs-help" style={{ marginTop: 2 }}>
          Firebase/GCP by service: {Object.entries(plane.gcp.byService).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([svc, usd]) => `${svc} ${fmtUsd2(usd)}`).join(' · ')}
        </p>
      )}
      {idle.length > 0 && (
        <div className="ap-banner is-warn" style={{ marginTop: 10 }}>
          {idle.length} live instance{idle.length > 1 ? 's' : ''} with no authenticated activity (idle) — still costing {fmtUsd2(idle.reduce((a, { c }) => a + (c?.burnPerDayUsd || 0), 0))}/day. Suspend to stop the clock.
        </div>
      )}
      {walletRows.length > 0 && (
        <div className="cs-card aa-tablewrap" style={{ marginTop: 12 }}>
          <div className="cs-help" style={{ padding: '8px 10px 0' }}>
            Model credits · {walletRows.length} org{walletRows.length === 1 ? '' : 's'} · {fmtUsd2(walletTotals.spent)} hosted model spend against {fmtUsd2(walletTotals.included)} included · {fmtUsd2(walletTotals.debited)} drawn from prepaid · {fmtUsd2(walletTotals.balance)} prepaid outstanding
            {walletRows.some((w) => w.exhausted) ? ` · ${walletRows.filter((w) => w.exhausted).length} exhausted (hosted calls returning 402)` : ''}
          </div>
          <table className="aa-table">
            <thead><tr><th>Org</th><th>Plan</th><th>Included</th><th>Spent</th><th>Prepaid balance</th><th>Remaining</th><th>Lifetime bought</th><th>State</th></tr></thead>
            <tbody>
              {walletRows.map((w) => (
                <tr key={w.orgId}>
                  <td><Link to={`/orgs/${w.orgId}/administration?tab=models`}>{w.name}</Link></td>
                  <td>{w.plan}</td>
                  <td>{fmtUsd2(w.includedUsd)}</td>
                  <td>{fmtUsd2(w.spentUsd)}</td>
                  <td>{fmtUsd2(w.balanceUsd)}</td>
                  <td>{fmtUsd2(w.remainingUsd)}</td>
                  <td>{fmtUsd2(w.lifetimeUsd)}</td>
                  <td>{w.exhausted ? <span className="ap-badge is-err">exhausted{w.exhaustedAt ? ` · ${fmtDate(w.exhaustedAt)}` : ''}</span> : w.remainingUsd < (w.includedUsd + w.balanceUsd + w.debitedUsd) * 0.2 ? <span className="ap-badge">low</span> : <span className="ap-badge is-ok">ok</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="cs-card aa-tablewrap" data-nova-note="Per app: status and where it runs beside what it costs — infra actuals, model estimate, Dev Console cents, burn, projection, revenue, margin, activity">
        <table className="aa-table">
          <thead>
            <tr>
              <th>App</th><th>Status</th><th>Runs on</th><th>MTD</th><th>Infra</th><th>Models</th><th>Dev</th>
              <th>Burn / day</th><th>Projected</th><th>Revenue</th><th>Margin</th><th>Activity</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ inst, c }) => (
              <tr key={`${inst.orgId}/${inst.appId}`}>
                <td>
                  <Link to={`/orgs/${inst.orgId}/apps/${inst.appId}`}>{inst.name}</Link>{' '}
                  <span className="aa-mono cs-help">{inst.slug}</span>
                </td>
                <td>
                  <span className={`ap-badge ${inst.status === 'live' ? (c?.monitorState === 'down' ? 'is-err' : 'is-ok') : inst.status === 'terminated' ? 'is-err' : 'is-muted'}`}>{inst.status}{inst.status === 'live' && c?.monitorState ? ` · ${c.monitorState}` : ''}</span>
                </td>
                <td className="cs-help">
                  {(c?.delivery || inst.delivery) === 'external' ? <span className="ap-badge is-muted">external</span> : c?.hostId ? <span className="aa-mono">{c.hostId}</span> : 'managed'}
                  {c?.payer ? <span className="aa-mono" title="AWS payer account"> · {c.payer}</span> : ''}
                </td>
                <td>{fmtUsd2(c?.mtdUsd)}</td>
                <td title={topService(c?.infra?.byService || c?.byService)}>{fmtUsd2(c?.infra?.usd)} <span className="ap-badge is-ok" style={{ fontSize: 10 }}>actual</span></td>
                <td>{c?.model?.usd ? <>{fmtUsd2(c.model.usd)} <span className="ap-badge is-muted" style={{ fontSize: 10 }}>est</span></> : '—'}</td>
                <td>{c?.dev?.usd ? fmtUsd2(c.dev.usd) : '—'}</td>
                <td>{fmtUsd2(c?.burnPerDayUsd)}</td>
                <td>{fmtUsd2(c?.projectedUsd)}</td>
                <td>{c?.billing === 'internal' ? <span className="cs-help">internal</span> : fmtUsd2(c?.revenueUsd)}</td>
                <td style={{ color: c?.marginUsd != null && c.marginUsd < 0 ? 'var(--danger)' : undefined }}>{c?.billing === 'internal' ? '—' : fmtUsd2(c?.marginUsd)}</td>
                <td className="cs-help">
                  {c?.pool && <span className="ap-badge is-muted">warm pool</span>}
                  {c?.idle && <span className="ap-badge is-warn">idle</span>}
                  {c?.neverActive && <span className="ap-badge is-muted">never active</span>}
                  {!c?.idle && !c?.neverActive && c?.lastActivityAt && <span>{new Date(c.lastActivityAt).toLocaleDateString()}</span>}
                  {!c?.pool && !c?.lastActivityAt && !c?.neverActive && <span title="Activity is tracked once the instance runs a kit that reports it">—</span>}
                </td>
                <td>
                  {(c?.idle || c?.neverActive) && inst.status === 'live' && onAction && (
                    <button type="button" className="cs-btn cs-btn-sm" disabled={busy} onClick={() => onAction(inst, 'suspend')} data-agent-noclick>Suspend</button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={13} className="cs-help">No instances.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {hostRows.length > 0 && (
        <div className="cs-card aa-tablewrap" style={{ marginTop: 12 }}>
          <table className="aa-table">
            <thead><tr><th>Host</th><th>Status</th><th>Instances</th><th>Memory by instance (EMA, MB)</th></tr></thead>
            <tbody>
              {hostRows.map(([id, h]) => {
                const load = Object.entries(h.load || {}).sort((a, b) => (b[1].memMb || 0) - (a[1].memMb || 0));
                return (
                  <tr key={id}>
                    <td className="aa-mono">{id}</td>
                    <td>{h.status || '—'}</td>
                    <td>{(h.instances || []).length}</td>
                    <td className="cs-help">{load.length ? load.map(([slug, l]) => `${slug} ${Math.round(l.memMb || 0)}`).join(' · ') : 'no samples yet'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!anyData && (
        <p className="cs-help">
          No cost data yet. Costs flow once AWS resources are tagged
          (<code>scripts/tag-app-resources.sh &lt;slug&gt;</code>) and the{' '}
          <code>kit:slug</code> cost-allocation tag is activated in billing —
          Cost Explorer lags ~24h behind both.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Scans — queue baseline/active scans and browse findings per instance.

function FindingsPanel({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    apiGet(`/apps/admin/scans/${slug}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [slug]);

  if (err) return <p className="cs-help">Failed to load findings: {err}</p>;
  if (!data) return <p className="cs-help">Loading findings…</p>;

  const open = (data.findings || []).filter((f) => f.status === 'open');
  const resolved = (data.findings || []).filter((f) => f.status !== 'open');
  const bySev = (list) => SEVERITIES.flatMap((sev) => list.filter((f) => f.severity === sev));

  return (
    <div className="aa-findings">
      {(data.jobs || []).slice(0, 3).map((j) => (
        <p key={j.id} className="cs-help">
          {j.mode} scan {j.status} · {new Date(j.createdAt).toLocaleString()} · {(j.scanners || []).join(', ')}
          {j.summary?.errors?.length ? ` · errors: ${j.summary.errors.join(', ')}` : ''}
        </p>
      ))}
      {!open.length && <p className="cs-help">No open findings.</p>}
      {bySev(open).map((f) => (
        <div key={f.id} className={`aa-finding is-${f.severity}`}>
          <span className="aa-finding-sev">{f.severity}</span>
          <span className="aa-finding-name">{f.name}</span>
          <span className="aa-mono cs-help">{f.scanner}</span>
          {f.detail ? <p className="aa-finding-detail">{f.detail}</p> : null}
          <p className="cs-help aa-mono">{f.target} · first seen {fmtDate(f.firstSeenAt)}</p>
        </div>
      ))}
      {resolved.length ? (
        <p className="cs-help">{resolved.length} resolved finding{resolved.length === 1 ? '' : 's'} not shown.</p>
      ) : null}
    </div>
  );
}

function ScanRow({ inst, busy, onScan, onAssurance }) {
  const [open, setOpen] = useState(false);
  const [activeConfirm, setActiveConfirm] = useState(false);
  const [confirmSlug, setConfirmSlug] = useState('');
  const counts = inst.scan?.counts || {};

  return (
    <>
      <tr className="aa-row" onClick={() => setOpen((v) => !v)}>
        <td>{inst.name} <span className="aa-mono cs-help">{inst.slug}</span></td>
        <td onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${inst.assurance ? 'cs-btn-outline' : 'cs-btn-ghost'}`}
            disabled={busy}
            title={inst.assurance ? 'Assurance on — browser checks + monthly passive baseline. Click to turn off.' : 'Assurance off. Click to enable browser checks + the monthly passive baseline.'}
            onClick={() => onAssurance(inst, !inst.assurance)}
          >
            {inst.assurance ? 'on' : 'off'}
          </button>
        </td>
        <td>{inst.scan?.lastScanAt ? fmtAgo(inst.scan.lastScanAt) : 'never'}</td>
        <td>
          <span className="aa-sev-counts">
            {SEVERITIES.map((sev) => (
              counts[sev] ? <span key={sev} className={`aa-sev is-${sev}`}>{counts[sev]} {sev}</span> : null
            ))}
            {!SEVERITIES.some((s) => counts[s]) && <span className="cs-help">clean</span>}
          </span>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="aa-actions">
            <button
              type="button"
              className="cs-btn cs-btn-sm cs-btn-outline"
              disabled={busy}
              onClick={() => onScan(inst, 'baseline')}
            >
              baseline scan
            </button>
            <button
              type="button"
              className="cs-btn cs-btn-sm cs-btn-ghost"
              disabled={busy}
              onClick={() => setActiveConfirm(true)}
            >
              active…
            </button>
          </div>
        </td>
      </tr>
      {activeConfirm && (
        <tr className="aa-detail-row">
          <td colSpan={5} onClick={(e) => e.stopPropagation()}>
            <div className="aa-confirm">
              <span>
                An <strong>active</strong> scan sends attack payloads at{' '}
                <strong>{inst.urls?.app}</strong> — it can create junk data and
                trip rate limits. Only run against instances you own. Type the
                slug to confirm:
              </span>
              <input
                className="cs-input"
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                placeholder={inst.slug}
              />
              <button
                type="button"
                className="cs-btn cs-btn-sm"
                disabled={confirmSlug !== inst.slug || busy}
                onClick={() => { onScan(inst, 'active'); setActiveConfirm(false); setConfirmSlug(''); }}
              >
                Run active scan
              </button>
              <button
                type="button"
                className="cs-btn cs-btn-sm cs-btn-ghost"
                onClick={() => { setActiveConfirm(false); setConfirmSlug(''); }}
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
      {open && (
        <tr className="aa-detail-row">
          <td colSpan={5}><FindingsPanel slug={inst.slug} /></td>
        </tr>
      )}
    </>
  );
}

// The control plane's own row: scanFindings/plane — the hourly ECR image
// read (scanner 'image') plus the monthly baseline against
// apps.scan.planeTargets. No run buttons: the schedule owns it.
function PlaneScanRow({ scan }) {
  const [open, setOpen] = useState(false);
  const counts = scan?.counts || {};
  return (
    <>
      <tr className="aa-row" onClick={() => setOpen((v) => !v)} data-nova-note="nventr.io control plane: supply-chain findings from the ECR image scan plus its monthly baseline">
        <td>nventr.io <span className="aa-mono cs-help">plane</span></td>
        <td><span className="cs-help">image + baseline</span></td>
        <td>
          {scan?.lastScanAt ? fmtAgo(scan.lastScanAt) : 'never'}
          <span className="cs-help"> · image {scan?.imageScanAt ? fmtAgo(scan.imageScanAt) : (scan?.imageScanStatus || 'pending')}</span>
        </td>
        <td>
          <span className="aa-sev-counts">
            {SEVERITIES.map((sev) => (
              counts[sev] ? <span key={sev} className={`aa-sev is-${sev}`}>{counts[sev]} {sev}</span> : null
            ))}
            {!SEVERITIES.some((s) => counts[s]) && <span className="cs-help">{scan ? 'clean' : 'no data yet'}</span>}
          </span>
        </td>
        <td><span className="cs-help aa-mono" title={scan?.image || ''}>{scan?.image ? scan.image.replace(/^.*\//, '').slice(0, 40) : 'scheduled'}</span></td>
      </tr>
      {open && (
        <tr className="aa-detail-row">
          <td colSpan={5}><FindingsPanel slug="plane" /></td>
        </tr>
      )}
    </>
  );
}

function ScansTab({ instances, planeScan, baseImage, busy, onScan, onAssurance }) {
  const scannable = (instances || []).filter((i) => i.status === 'live' && i.urls?.app);
  const baseMoved = baseImage?.pendingKitRelease || baseImage?.pendingPlaneBuild;
  return (
    <div className="cs-card aa-tablewrap">
      <p className="cs-help" data-nova-note="Run controls for web scans; the full six-layer posture (runtime floor, base image, container images, web, CI gates, Dependabot) is the fleet Security posture board">
        Run controls only. The full posture — runtime floor, base image{baseMoved ? <span className="aa-warn-text"> (moved, release due)</span> : null}, container images, web, CI gates, Dependabot — is on the{' '}
        <Link to="/admin/fleet/security">Security posture board →</Link>
      </p>
      <table className="aa-table">
        <thead>
          <tr><th>App</th><th>Assurance</th><th>Last scan</th><th>Open findings</th><th>Run</th></tr>
        </thead>
        <tbody>
          <PlaneScanRow scan={planeScan} />
          {scannable.map((inst) => (
            <ScanRow key={`${inst.orgId}/${inst.appId}`} inst={inst} busy={busy} onScan={onScan} onAssurance={onAssurance} />
          ))}
          {!scannable.length && (
            <tr><td colSpan={5} className="cs-help">No live instances to scan.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Launch checklist: everything between "code is deployed" and "first real
// sale", with live status from GET /apps/admin/launch-status and in-app
// forms for each fixable item (secrets → SSM write-only, price IDs + kit pin
// + legal flag → live config, host registration → hosts inventory).

function CheckDot({ ok, warn }) {
  return <span className={`aa-checkdot ${ok ? 'is-ok' : warn ? 'is-warn' : 'is-miss'}`}>{ok ? '✓' : warn ? '!' : '✗'}</span>;
}

// Fleet capacity (shared/fleetLaunch.js): free slots vs the headroom floor,
// the launch in flight with its stage, and the operator "launch now". The
// monitor launches on its own when free < minFreeSlots; this card is the
// window onto that, plus the override.
function FleetCapacityCard({ fleet, launches, busy, onLaunch, onRetry }) {
  if (!fleet) return null;
  const inFlight = (launches || []).find((l) => l.status === 'queued' || l.status === 'running');
  const lastError = !inFlight ? (launches || []).find((l) => l.status === 'error') : null;
  const low = fleet.freeSlots != null && fleet.freeSlots < fleet.minFreeSlots;
  const stageIdx = inFlight ? (inFlight.stages || []).filter((s) => s.status === 'done').length : 0;
  return (
    <div className={`cs-card aa-fleet-cap ${low ? 'is-low' : ''}`} data-nova-note="Fleet capacity: free app slots across active hosts vs the minFreeSlots floor; a host launch in flight shows its stage">
      <div className="aa-fleet-cap-row">
        <div>
          <strong>{fleet.freeSlots ?? '—'}</strong> free slot{fleet.freeSlots === 1 ? '' : 's'} on {fleet.appHosts ?? '—'} host{fleet.appHosts === 1 ? '' : 's'}
          <span className="cs-help"> · floor {fleet.minFreeSlots} · max {fleet.maxHosts} hosts · {fleet.instanceType}</span>
          {fleet.autoLaunch === false ? <span className="aa-warn-text"> · auto-launch OFF (apps.fleet.autoLaunch)</span> : null}
        </div>
        <div className="aa-actions" data-agent-guard>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || !!inFlight} onClick={onLaunch}
            title="RunInstances from the template host → Elastic IP → SSM → bootstrap → register → disk alarm. ~10 min. Bypasses the floor, not maxHosts.">
            {inFlight ? 'Launching…' : 'Launch a host now'}
          </button>
        </div>
      </div>
      {inFlight ? (
        <p className="cs-help">
          <span className="aa-health-badge is-warn">launching</span>{' '}
          {inFlight.instanceId ? <code>{inFlight.instanceId}</code> : null} {inFlight.az} · stage <code>{inFlight.stage || 'queued'}</code> ({stageIdx}/6)
          {' · '}{inFlight.reason} · requested {fmtAgo(inFlight.requestedAt)}
        </p>
      ) : null}
      {lastError ? (
        <p className="cs-help">
          <span className="aa-health-badge is-down">launch failed</span>{' '}
          {lastError.instanceId ? <code>{lastError.instanceId}</code> : null} at <code>{lastError.stage || '?'}</code>: {lastError.error}
          {' '}
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onRetry(lastError.id)}>retry</button>
        </p>
      ) : null}
      {low && !inFlight && !lastError ? (
        <p className="cs-help aa-warn-text">Below the floor — the monitor launches on its next tick{fleet.autoLaunch === false ? ' (blocked: auto-launch is off)' : ''}.</p>
      ) : null}
    </div>
  );
}

function SecretRow({ id, label, help, done, busy, onSave }) {
  const [value, setValue] = useState('');
  return (
    <div className="aa-launch-row">
      <CheckDot ok={done} />
      <div className="aa-launch-main">
        <strong>{label}</strong>
        <span className="cs-help">{help}</span>
        <div className="aa-launch-form">
          <input
            type="password"
            className="aa-input"
            placeholder={done ? 'set — paste to replace' : 'paste the key…'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            className="cs-btn cs-btn-sm"
            disabled={busy || value.trim().length < 8}
            onClick={async () => { await onSave(id, value.trim()); setValue(''); }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function LaunchPanel({ notify }) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kitRef, setKitRef] = useState('');
  const [priceStarter, setPriceStarter] = useState('');
  const [priceGrowth, setPriceGrowth] = useState('');
  const [hostId, setHostId] = useState('');
  const [hostIp, setHostIp] = useState('');
  const [bootstrap, setBootstrap] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await apiGet('/apps/admin/launch-status');
      setStatus(s);
      setErr(null);
      setKitRef(s.kitRef || '');
      setPriceStarter(s.stripePriceIds?.starter || '');
      setPriceGrowth(s.stripePriceIds?.growth || '');
    } catch (e) {
      setErr(e.status === 403 ? 'Operator access required.' : e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      notify(okMsg);
      await load();
    } catch (e) {
      notify(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const saveSecret = (key, value) => act(
    () => apiPost('/apps/admin/launch-secret', { key, value }),
    `${key} saved to SSM (write-only — the value is never shown again).`
  );

  if (err) return <div className="cs-card"><p className="cs-help">{err}</p></div>;
  if (!status) return <div className="cs-card"><p className="cs-help">Loading launch status…</p></div>;

  const priceIdsDone = !!(status.stripePriceIds?.starter && status.stripePriceIds?.growth);
  const hostsDone = (status.hosts || []).length > 0;

  return (
    <>
      <h2>
        Launch checklist{' '}
        <span className="cs-help">what's left between the deployed pipeline and the first real sale</span>
      </h2>

      <div className="cs-card aa-launch-card">
        <h3>1 · Secrets (SSM SecureString, write-only)</h3>
        <SecretRow
          id="github-token"
          label="GitHub token"
          help="/nventr-io/github-token — hosts and runners clone the kit with it. Needs repo read on the kit repo."
          done={status.secrets['github-token']}
          busy={busy}
          onSave={saveSecret}
        />
        <SecretRow
          id="cursor-api-key"
          label="Cursor API key"
          help="/nventr-io/cursor-api-key — dropped into each instance so Dev Console agent sessions work. One shared account across buyers for now."
          done={status.secrets['cursor-api-key']}
          busy={busy}
          onSave={saveSecret}
        />
        <SecretRow
          id="cursor-admin-key"
          label="Cursor Admin API key (optional)"
          help="/<plane>/cursor-admin-key — a team Admin API key (Cursor dashboard → Settings → Admin API). Wakes the nightly account reconcile so Dev Console spend in Economics is checked against the invoice. The user key above is not enough."
          done={status.secrets['cursor-admin-key']}
          busy={busy}
          onSave={saveSecret}
        />
        <SecretRow
          id="stripe-secret-key"
          label="Stripe secret key"
          help="/nventr-io/stripe-secret-key — checkout + invoices. Use the live key only when you mean it."
          done={status.secrets['stripe-secret-key']}
          busy={busy}
          onSave={saveSecret}
        />
        <SecretRow
          id="vault-key"
          label="Vault master key"
          help="/<plane>/vault-key — seals every entry in the customer Key Vault (Administration → Vault). 32 random bytes as hex: `openssl rand -hex 32`. Set once; the server refuses to overwrite it."
          done={status.secrets['vault-key']}
          busy={busy}
          onSave={saveSecret}
        />
        <div className="aa-launch-row">
          <CheckDot ok={status.secrets['layer-git-key']} />
          <div className="aa-launch-main">
            <strong>Layer git key</strong>
            <span className="cs-help">
              /nventr-io/layer-git-key — the layer-scoped IAM key instance containers use for the customer
              repo. Provisioned from IAM (user nventr-layer-git), not paste-able here.
            </span>
          </div>
        </div>
      </div>

      <div className="cs-card aa-launch-card">
        <h3>2 · Stripe price IDs <CheckDot ok={priceIdsDone} /></h3>
        <p className="cs-help">
          Create the two recurring products in Stripe (Kit $149/mo, Kit Business $349/mo — keys stay
          starter/growth) and paste the price IDs. Written to apps.priceBook.stripePriceIds.
        </p>
        <div className="aa-launch-form">
          <input className="aa-input" placeholder="price_… (starter)" value={priceStarter} onChange={(e) => setPriceStarter(e.target.value)} />
          <input className="aa-input" placeholder="price_… (growth)" value={priceGrowth} onChange={(e) => setPriceGrowth(e.target.value)} />
          <button
            type="button"
            className="cs-btn cs-btn-sm"
            disabled={busy}
            onClick={() => act(
              () => apiPost('/apps/admin/launch-config', { stripePriceIds: { starter: priceStarter, growth: priceGrowth } }),
              'Stripe price IDs saved — live within ~1 minute.'
            )}
          >
            Save
          </button>
        </div>
      </div>

      <div className="cs-card aa-launch-card">
        <h3>3 · Kit release pin <CheckDot ok={status.kitRefPinned} warn={!status.kitRefPinned} /></h3>
        <p className="cs-help">
          apps.kitRef pins which kit version customers get ({status.kitRepo}). It is currently{' '}
          <code>{status.kitRef}</code>{!status.kitRefPinned && ' — “main” moves under customers; tag a release on the kit repo (e.g. v1.0.0) and pin it here before selling'}.
        </p>
        <div className="aa-launch-form">
          <input className="aa-input" placeholder="v1.0.0" value={kitRef} onChange={(e) => setKitRef(e.target.value)} />
          <button
            type="button"
            className="cs-btn cs-btn-sm"
            disabled={busy || !kitRef.trim()}
            onClick={() => act(
              () => apiPost('/apps/admin/launch-config', { kitRef: kitRef.trim() }),
              `Kit pinned to ${kitRef.trim()}.`
            )}
          >
            Pin
          </button>
        </div>
      </div>

      <div className="cs-card aa-launch-card">
        <h3>4 · Legal review <CheckDot ok={status.legalReviewed} /></h3>
        <p className="cs-help">
          legal/tos.md, aup.md, pricing.md are drafted placeholders ({status.agreementsSeeded} agreement
          {status.agreementsSeeded === 1 ? '' : 's'} seeded, v1). Have counsel review them, re-seed, then
          record it here — this flag is an acknowledgment, not a substitute for the review.
        </p>
        <label className="aa-launch-check">
          <input
            type="checkbox"
            checked={status.legalReviewed}
            disabled={busy}
            onChange={(e) => act(
              () => apiPost('/apps/admin/launch-config', { legalReviewed: e.target.checked }),
              e.target.checked ? 'Legal review recorded.' : 'Legal review flag cleared.'
            )}
          />
          Counsel has reviewed the customer agreements
        </label>
      </div>

      <div className="cs-card aa-launch-card">
        <h3>5 · Instance host <CheckDot ok={hostsDone} /></h3>
        <p className="cs-help">
          {hostsDone
            ? `${status.hosts.length} active host${status.hosts.length === 1 ? '' : 's'} (${status.hosts.map((h) => `${h.hostId} ${h.count}/${h.capacity}`).join(', ')}).`
            : 'No hosts — customer instances have nowhere to run. Launch an EC2 box (Ubuntu 22.04+, instance profile with SSM core + the codecommit/ssm grants + cloudwatch:PutMetricData), run the bootstrap script on it as root, then register it here.'}
        </p>
        <div className="aa-launch-form">
          <button
            type="button"
            className="cs-btn cs-btn-sm cs-btn-outline"
            onClick={async () => {
              if (!bootstrap) {
                const b = await apiGet('/apps/admin/host-bootstrap');
                setBootstrap(b.script);
              } else setBootstrap(null);
            }}
          >
            {bootstrap ? 'Hide bootstrap script' : 'Show bootstrap script'}
          </button>
          {bootstrap ? (
            <button
              type="button"
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={() => navigator.clipboard.writeText(bootstrap).then(() => notify('Bootstrap script copied.'))}
            >
              Copy
            </button>
          ) : null}
        </div>
        {bootstrap ? <pre className="aa-bootstrap">{bootstrap}</pre> : null}
        <div className="aa-launch-form" style={{ marginTop: 10 }}>
          <input className="aa-input" placeholder="i-0abc123…" value={hostId} onChange={(e) => setHostId(e.target.value)} />
          <input className="aa-input" placeholder="public IP" value={hostIp} onChange={(e) => setHostIp(e.target.value)} />
          <button
            type="button"
            className="cs-btn cs-btn-sm"
            disabled={busy || !hostId.trim() || !hostIp.trim()}
            onClick={() => act(
              () => apiPost('/apps/admin/hosts/register', { instanceId: hostId.trim(), publicIp: hostIp.trim() }),
              `Host ${hostId.trim()} registered.`
            )}
          >
            Register host
          </button>
        </div>
      </div>
    </>
  );
}

// Referral codes (backend shared/billing/referrals.js): one string you hand
// to a person. Credits, a comped App Kit plan, a comped Platform plan — any
// mix. Redeemed at /start?ref=CODE, /orgs?ref=CODE, or Settings › Billing.
function ReferralCodesCard({ notify }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: '', label: '', usd: 20, kit: 'starter', platform: '', days: '', maxUses: '' });
  const origin = window.location.origin;
  const load = useCallback(() => apiGet('/apps/admin/referrals').then(setData).catch((e) => notify(`referrals: ${e.message}`)), [notify]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiPost('/apps/admin/referrals', {
        code: form.code, label: form.label, usd: Number(form.usd) || 0, kit: form.kit || null, platform: form.platform || null,
        days: Number(form.days) || null, maxUses: Number(form.maxUses) || null,
      });
      notify(`Code ${r.code} is live — share ${origin}/start?plan=kit&ref=${r.code}`);
      setForm((f) => ({ ...f, code: '', label: '' }));
      await load();
    } catch (err) {
      notify(`code not created: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  const disable = async (code) => {
    if (!window.confirm(`Disable ${code}? Orgs that already redeemed it keep what they got.`)) return;
    setBusy(true);
    try { await apiDelete(`/apps/admin/referrals/${code}`); notify(`${code} disabled`); await load(); } catch (err) { notify(err.message); } finally { setBusy(false); }
  };
  const grants = (c) => [c.usd ? `$${c.usd} credits` : null, c.kit ? `Kit ${c.kit}` : null, c.platform ? `Platform ${c.platform}` : null].filter(Boolean).join(' · ') || '—';
  return (
    <div className="cs-card aa-launch-card" data-nova-note="Referral codes: mint a code that grants credits, a comped App Kit plan or a comped Platform plan; see uses per code">
      <h3>Referral codes</h3>
      <p className="cs-help">
        Hand a code to a teammate or a prospect: they sign up at <code>{origin}/start?plan=kit&amp;ref=CODE</code> (or paste it under Settings › Billing) and get what the code carries — no card. One redemption per organization per code.
      </p>
      <form className="aa-launch-form" onSubmit={create} data-agent-guard>
        <input className="aa-input" style={{ minWidth: 140, textTransform: 'uppercase' }} placeholder="CODE" value={form.code} onChange={set('code')} aria-label="Code" required />
        <input className="aa-input" style={{ minWidth: 160 }} placeholder="label (who / why)" value={form.label} onChange={set('label')} aria-label="Label" />
        <label className="cs-help">credits $<input className="aa-input" style={{ minWidth: 70, width: 70 }} type="number" min="0" value={form.usd} onChange={set('usd')} aria-label="Credits USD" /></label>
        <label className="cs-help">kit <select className="aa-input" style={{ minWidth: 110 }} value={form.kit} onChange={set('kit')} aria-label="Kit plan"><option value="">none</option><option value="starter">starter ($149)</option><option value="growth">growth ($349)</option></select></label>
        <label className="cs-help">platform <select className="aa-input" style={{ minWidth: 110 }} value={form.platform} onChange={set('platform')} aria-label="Platform plan"><option value="">none</option><option value="start">start</option><option value="team">team</option><option value="business">business</option></select></label>
        <label className="cs-help">days <input className="aa-input" style={{ minWidth: 70, width: 70 }} type="number" min="0" placeholder="∞" value={form.days} onChange={set('days')} aria-label="Days" /></label>
        <label className="cs-help">max uses <input className="aa-input" style={{ minWidth: 70, width: 70 }} type="number" min="0" placeholder="∞" value={form.maxUses} onChange={set('maxUses')} aria-label="Max uses" /></label>
        <button type="submit" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || !form.code.trim()}>Create code</button>
      </form>
      {data && (
        <table className="aa-table" style={{ marginTop: 10 }}>
          <thead><tr><th>Code</th><th>Label</th><th>Grants</th><th>Term</th><th>Uses</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.codes.map((c) => (
              <tr key={c.code}>
                <td className="aa-mono">{c.code}</td>
                <td>{c.label || ''}</td>
                <td>{grants(c)}</td>
                <td>{c.days ? `${c.days} days` : 'until revoked'}{c.expiresAt ? ` · code expires ${new Date(c.expiresAt).toLocaleDateString()}` : ''}</td>
                <td title={(c.orgs || []).join(', ')}>{c.uses}{c.maxUses ? `/${c.maxUses}` : ''}</td>
                <td><span className={`aa-health-badge is-${c.disabled ? 'down' : c.live ? 'up' : 'warn'}`}>{c.disabled ? 'disabled' : c.live ? 'live' : 'expired'}</span></td>
                <td>{!c.disabled && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => disable(c.code)} data-agent-noclick>disable</button>}</td>
              </tr>
            ))}
            {!data.codes.length && <tr><td colSpan={7} className="cs-help">No codes yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function AdminApps() {
  // ?tab=platform deep-links from the platform-degraded alert mail.
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get('tab'); return TABS.includes(t) ? t : 'instances'; });
  const [instances, setInstances] = useState(null);
  const [planeScan, setPlaneScan] = useState(null);
  const [baseImage, setBaseImage] = useState(null);
  // CodeCommit shards the fleet's layer repos spread over (apps.gitShards).
  const [gitShards, setGitShards] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [costData, setCostData] = useState(null);
  const [hosts, setHosts] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [launches, setLaunches] = useState([]);
  const fleetVitals = useVitals(tab === 'hosts' ? '/apps/admin/vitals' : null);
  const [standby, setStandby] = useState(null);
  const [priceBook, setPriceBook] = useState(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [byOrg, setByOrg] = useState(false);
  const [pinnedKitRef, setPinnedKitRef] = useState('');
  const cmd = useCommandToggle('fleet');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [i, inc, co, h, p, ls, sb] = await Promise.all([
        apiGet('/apps/admin/instances'),
        apiGet('/apps/admin/incidents').catch(() => ({ incidents: [] })),
        apiGet('/apps/admin/costs').catch(() => null),
        apiGet('/apps/admin/hosts').catch(() => ({ hosts: [] })),
        apiGet('/apps/admin/pricebook').catch(() => ({ priceBook: null })),
        apiGet('/apps/admin/launch-status').catch(() => ({})),
        apiGet('/apps/admin/standby').catch(() => ({ standby: null })),
      ]);
      setPinnedKitRef(ls.kitRef || 'main');
      setInstances(i.instances || []);
      setPlaneScan(i.planeScan || null);
      setBaseImage(i.baseImage || null);
      setGitShards(i.gitShards || null);
      setIncidents(inc.incidents || []);
      setCostData(co);
      setHosts(h.hosts || []);
      setFleet(h.fleet || null);
      setLaunches(h.launches || []);
      setStandby(sb.standby || null);
      setPriceBook(p.priceBook);
      setPriceDraft(p.priceBook ? JSON.stringify(p.priceBook, null, 2) : '');
    } catch (err) {
      setError(err.status === 403 ? 'Operator access required.' : err.message);
      setInstances([]);
    }
  }, []);

  useEffect(() => {
    if (!isBackendConfigured()) {
      setError('Backend API is not configured (REACT_APP_UPLOAD_API).');
      return undefined;
    }
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const onAction = async (inst, action, extra = {}) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`/apps/admin/instances/${inst.orgId}/${inst.appId}/action`, { action, ...extra });
      setNotice(`${action} queued for ${inst.slug}`);
      await load();
    } catch (err) {
      setNotice(`${action} failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onRevokeLicense = async (inst) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`/apps/admin/licenses/${inst.licenseId}/revoke`, {});
      setNotice(`license revoked for ${inst.slug} — the instance blocks at its next validation.`);
      await load();
    } catch (err) {
      setNotice(`revoke failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onAssurance = async (inst, enabled) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`/apps/admin/instances/${inst.orgId}/${inst.appId}/assurance`, { enabled });
      setNotice(`Assurance ${enabled ? 'enabled' : 'disabled'} for ${inst.slug} — browser checks and the monthly baseline follow this flag.`);
    } catch (err) {
      setNotice(`assurance toggle failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onScan = async (inst, mode) => {
    setBusy(true);
    setNotice(null);
    try {
      const { jobId } = await apiPost(`/apps/admin/instances/${inst.orgId}/${inst.appId}/scan`, { mode });
      setNotice(`${mode} scan queued for ${inst.slug} (job ${jobId})`);
    } catch (err) {
      setNotice(`scan failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onLaunchHost = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await apiPost('/apps/admin/hosts/launch', {});
      setNotice(`Launching a fleet host in ${r.az} (${r.launchId}) — about 10 minutes to active; the monitor drives it.`);
      await load();
    } catch (err) {
      setNotice(`launch refused: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onRetryLaunch = async (id) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost(`/apps/admin/hosts/launch/${id}/retry`, {});
      setNotice(`Launch ${id} resumes from its failed stage.`);
      await load();
    } catch (err) {
      setNotice(`retry refused: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onPinKitRef = async (kitRef) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiPost('/apps/admin/launch-config', { kitRef });
      setPinnedKitRef(kitRef);
      setNotice(`apps.kitRef pinned to ${kitRef} — reprovisions check it out from now on`);
    } catch (err) {
      setNotice(`pin failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const savePriceBook = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const parsed = JSON.parse(priceDraft);
      await apiPost('/apps/admin/pricebook', { priceBook: parsed });
      setNotice('Price book saved — live within ~1 minute.');
      setPriceBook(parsed);
    } catch (err) {
      setNotice(err instanceof SyntaxError ? `Invalid JSON: ${err.message}` : `Save failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const provisioningCount = useMemo(
    () => (instances || []).filter((i) => i.status === 'provisioning').length,
    [instances]
  );
  const downCount = useMemo(
    () => (instances || []).filter((i) => i.status === 'live' && i.monitor?.state === 'down').length,
    [instances]
  );

  if (error) {
    return (
      <div className="cs-page aa-page">
        <h1>Fleet admin</h1>
        <div className="cs-card"><p className="cs-help">{error}</p></div>
      </div>
    );
  }

  return (
    <div className="cs-page aa-page">
      <div className="aa-head">
        <h1>Fleet admin</h1>
        {cmd.supported && <span style={{ marginLeft: 'auto', marginRight: 12 }}><CommandToggle on={cmd.on} onChange={cmd.set} labels={['Command', 'Tabs']} /></span>}
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={load} disabled={busy}>
          Refresh
        </button>
      </div>
      {notice ? <p className="aa-notice">{notice}</p> : null}

      {cmd.on && (
        <AdminFleetCommand
          instances={instances}
          incidents={incidents}
          costData={costData}
          hosts={hosts}
          standby={standby}
          pinnedKitRef={pinnedKitRef}
          busy={busy}
          onAction={onAction}
          onRevokeLicense={onRevokeLicense}
          onScan={onScan}
          InstanceRow={InstanceRow}
          chrome={notice ? 190 : 150}
        />
      )}

      {!cmd.on && (
      <>
      <div className="aa-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`aa-tab ${tab === t ? 'is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
            {t === 'instances' && provisioningCount ? <span className="aa-tab-dot is-info">{provisioningCount}</span> : null}
            {t === 'health' && downCount ? <span className="aa-tab-dot is-err">{downCount}</span> : null}
          </button>
        ))}
      </div>

      {tab === 'launch' && (
        <section className="aa-section">
          <LaunchPanel notify={setNotice} />
          <ReferralCodesCard notify={setNotice} />
        </section>
      )}

      {tab === 'releases' && (
        <section className="aa-section">
          <AdminReleases notify={setNotice} />
        </section>
      )}

      {tab === 'platform' && (
        <section className="aa-section">
          <AdminPlatformHealth notify={setNotice} />
        </section>
      )}

      {tab === 'ipmap' && (
        <section className="aa-section">
          <h2>
            How the kit and customer IP stay separate{' '}
            <span className="cs-help">the rented-kit model — full spec in the kit's docs/layer-contract.md</span>
          </h2>
          <div className="cs-card">
            <p className="cs-help" style={{ marginBottom: 12 }}>
              Teal is what you rent. Green is what you own. They live in different
              repos and only meet inside a running app. The build gate
              refuses anything outside the extension seams, and a commit
              cannot include kit code.
            </p>
            <KitIpDiagram />
          </div>
        </section>
      )}

      {tab === 'instances' && (
        <section className="aa-section">
          <h2>
            Instances{' '}
            <span className="cs-help">
              {instances ? `${instances.length} total · ${provisioningCount} provisioning` : '…'}
            </span>
            <span className="aa-view-toggle">
              <button type="button" className={`aa-tab ${byOrg ? '' : 'is-active'}`} onClick={() => setByOrg(false)}>list</button>
              <button type="button" className={`aa-tab ${byOrg ? 'is-active' : ''}`} onClick={() => setByOrg(true)}>by org</button>
            </span>
          </h2>
          {byOrg ? (
            <AdminFleetByOrg
              instances={instances}
              gitShards={gitShards}
              pinnedKitRef={pinnedKitRef}
              busy={busy}
              onReprovision={(inst) => onAction(inst, 'reprovision')}
              onPinKitRef={onPinKitRef}
            />
          ) : (
          <div className="cs-card aa-tablewrap">
            <table className="aa-table">
              <thead>
                <tr>
                  <th>Name</th><th>Slug</th><th>Plan</th><th>Status</th>
                  <th>Deploy</th><th>License</th><th>Host</th><th>Prepaid until</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(instances || []).map((inst) => (
                  <InstanceRow key={`${inst.orgId}/${inst.appId}`} inst={inst} onAction={onAction} onRevokeLicense={onRevokeLicense} busy={busy} />
                ))}
                {instances && !instances.length && (
                  <tr><td colSpan={9} className="cs-help">No instances yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
          <p className="cs-help">
            Adopt an externally-hosted kit app with{' '}
            <code>node scripts/register-app.mjs --org &lt;orgId&gt; --slug &lt;slug&gt; --url https://…</code>
            {' '}— it appears here as <em>external</em> and joins monitoring/scans.
          </p>
        </section>
      )}

      {tab === 'health' && (
        <section className="aa-section">
          <HealthTab instances={instances} incidents={incidents} />
        </section>
      )}

      {tab === 'costs' && (
        <section className="aa-section">
          <CostsTab instances={instances} costs={costData?.costs} period={costData?.period} plane={costData?.plane} planes={costData?.planes} businessUsd={costData?.businessUsd} hosts={costData?.hosts} wallets={costData?.wallets} ledger={costData?.ledger} tokens={costData?.tokens} onAction={onAction} busy={busy} />
        </section>
      )}

      {tab === 'scans' && (
        <section className="aa-section">
          <ScansTab instances={instances} planeScan={planeScan} baseImage={baseImage} busy={busy} onScan={onScan} onAssurance={onAssurance} />
        </section>
      )}

      {tab === 'hosts' && (
        <section className="aa-section">
          <h2>Hosts</h2>
          <FleetCapacityCard fleet={fleet} launches={launches} busy={busy} onLaunch={onLaunchHost} onRetry={onRetryLaunch} />
          <div className="cs-card aa-tablewrap">
            <table className="aa-table">
              <thead>
                <tr>
                  <th>Host</th><th>Public IP</th><th>Slots</th><th>Disk</th><th>Memory</th>
                  <th>Load</th><th>Agent</th><th>Last prune</th><th>Status</th><th>Rebalance</th>
                </tr>
              </thead>
              <tbody>
                {(hosts || []).map((h) => {
                  const s = h.stats || {};
                  const agentUp = h.agentAt && Date.now() - h.agentAt < 90 * 1000;
                  const diskState = s.diskPct == null ? 'unknown' : s.diskPct >= 80 ? 'down' : s.diskPct >= 70 ? 'warn' : 'up';
                  return (
                    <tr key={h.hostId}>
                      <td className="aa-mono">{h.instanceId}</td>
                      <td className="aa-mono">{h.publicIp}</td>
                      <td title={h.siteCount ? `${h.count} running stack(s) · ${h.siteCount} parked in site mode (ports held, no containers)` : undefined}>
                        {h.count}/{h.capacity}{h.siteCount ? <span className="cs-help"> · {h.siteCount} site</span> : null}
                      </td>
                      <td>
                        <span className={`aa-health-badge is-${diskState}`} title={s.dockerDiskPct != null ? `/var/lib/docker ${s.dockerDiskPct}%` : ''}>
                          {s.diskPct != null ? `${s.diskPct}%` : '—'}
                        </span>
                      </td>
                      <td className={s.memUsedPct >= 85 ? 'aa-warn-text' : ''} title={h.vitals?.detail || ''}>
                        {s.memUsedPct != null ? `${s.memUsedPct}%` : '—'}
                        {s.swapUsedMb > 0 ? <span className="cs-help"> +{s.swapUsedMb} MB swap</span> : null}
                        <Spark points={hostMemory(fleetVitals?.hosts?.[h.hostId]?.rows)} max={100} warnAt={fleetVitals?.thresholds?.hostMemWarnPct ?? 85} unit="%" title={`host memory, last 24 h`} />
                      </td>
                      <td>{s.load1 != null ? s.load1 : '—'}</td>
                      <td>
                        <span className={`aa-health-badge is-${agentUp ? 'up' : 'down'}`}>{agentUp ? 'polling' : 'silent'}</span>{' '}
                        <span className="cs-help">{h.statsAt ? fmtAgo(h.statsAt) : ''}</span>
                      </td>
                      <td title={h.prune?.tail || ''}>
                        {h.prune?.lastAt ? `${fmtAgo(h.prune.lastAt)} · ${h.prune.reason || ''}` : '—'}
                      </td>
                      <td>{h.status}{h.vitals?.state && h.vitals.state !== 'ok' ? <div className="aa-warn-text">{h.vitals.state}</div> : null}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {h.rebalance?.proposed ? (
                          <div className="aa-actions" data-agent-guard>
                            <span className="cs-help" title={`${h.rebalance.proposed.memMb} MB stack; target lands at ~${h.rebalance.proposed.toPctAfter}%`}>
                              move <code>{h.rebalance.proposed.slug}</code> → {h.rebalance.proposed.to}
                            </span>
                            <button
                              type="button"
                              className="cs-btn cs-btn-sm cs-btn-outline"
                              disabled={busy}
                              title="Backup → stamp on the target → restore → DNS repoint → verify → tear down here. The app pauses for about a minute during the restore."
                              onClick={() => onAction({ orgId: h.rebalance.proposed.orgId, appId: h.rebalance.proposed.appId, slug: h.rebalance.proposed.slug }, 'migrate', { targetHostId: h.rebalance.proposed.to, reason: `operator rebalance from ${h.hostId}` })}
                            >
                              migrate
                            </button>
                          </div>
                        ) : (
                          <span className="cs-help">{h.rebalance?.at ? 'balanced' : '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {hosts && !hosts.length && (
                  <tr>
                    <td colSpan={10} className="cs-help">
                      No hosts registered — run <code>node scripts/register-host.mjs</code>.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'mail' && (
        <section className="aa-section">
          <AdminMailPlanes notify={setNotice} />
        </section>
      )}

      {tab === 'pricebook' && (
        <section className="aa-section">
          <h2>Price book <span className="cs-help">live config apps.priceBook — applies in ~1 min</span></h2>
          <div className="cs-card">
            {priceBook === null ? (
              <p className="cs-help">Price book unavailable.</p>
            ) : (
              <>
                <textarea
                  className="aa-pricebook"
                  value={priceDraft}
                  onChange={(e) => setPriceDraft(e.target.value)}
                  spellCheck={false}
                  rows={18}
                />
                <div className="aa-actions" style={{ marginTop: 10 }}>
                  <button type="button" className="cs-btn cs-btn-sm" onClick={savePriceBook} disabled={busy}>
                    Save price book
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-sm cs-btn-ghost"
                    onClick={() => setPriceDraft(JSON.stringify(priceBook, null, 2))}
                    disabled={busy}
                  >
                    Reset
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}
      </>
      )}
    </div>
  );
}
